import { useEffect, useRef, useState } from "react";
import api from "../lib/api";
import PageHeader from "../components/PageHeader";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { ScanLine, Upload, Camera, Trash2, Eye, Loader2, FolderSync, PackagePlus, AlertTriangle } from "lucide-react";
import ItemAutocomplete from "../components/ItemAutocomplete";
import { toast } from "sonner";

const fmt = (v) => v == null || v === "" ? "—" : (Number(v) || 0).toLocaleString("de-DE", { style: "currency", currency: "EUR" });
const TYPE_LABEL = { eingangsrechnung: "Eingangsrechnung", lieferschein: "Lieferschein", gutschrift: "Gutschrift" };
const STATUS_CLS = (s) => s === "assigned" ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-800";

export default function Belegscan() {
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [docType, setDocType] = useState("auto");
  const [suppliers, setSuppliers] = useState([]);
  const [preview, setPreview] = useState(null);
  const [fileUrl, setFileUrl] = useState(null);
  const [fileMime, setFileMime] = useState("");
  const [saving, setSaving] = useState(false);
  const [ftpOpen, setFtpOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [receipt, setReceipt] = useState(null);
  const [receiptSaving, setReceiptSaving] = useState(false);
  const fileRef = useRef(null);
  const camRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/inbound/documents", { params: filter === "all" ? {} : { status: filter } });
      setRows(r.data || []);
    } catch (e) { toast.error("Belege konnten nicht geladen werden"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);
  useEffect(() => { api.get("/suppliers").then((r) => setSuppliers(r.data || [])).catch(() => {}); }, []);
  useEffect(() => { api.get("/items").then((r) => setItems(r.data || [])).catch(() => {}); }, []);

  const openReceipt = async (r) => {
    try {
      const res = await api.get(`/inbound/documents/${r.id}/receipt-draft`);
      const lines = (res.data.lines || []).map((l) => ({
        description: l.description,
        item_id: l.suggested_item?.id || "",
        quantity: l.quantity || "",
        mhd: "",
        location: l.suggested_item?.location || "",
      }));
      setReceipt({ docId: r.id, meta: res.data, lines, already: res.data.already_received });
    } catch (e) { toast.error("Wareneingang-Entwurf fehlgeschlagen"); }
  };

  const setRL = (i, k, v) => setReceipt((rc) => {
    const lines = rc.lines.map((l, idx) => idx === i ? { ...l, [k]: v } : l);
    return { ...rc, lines };
  });

  const confirmReceipt = async () => {
    const valid = receipt.lines.filter((l) => l.item_id && Number(l.quantity) > 0);
    if (!valid.length) { toast.error("Mind. eine Position mit Artikel und Menge nötig"); return; }
    setReceiptSaving(true);
    try {
      const res = await api.post(`/inbound/documents/${receipt.docId}/goods-receipt`, {
        lines: valid.map((l) => ({ item_id: l.item_id, quantity: Number(l.quantity), mhd: l.mhd, location: l.location })),
      });
      toast.success(`${res.data.created_batches.length} Position(en) als Wareneingang gebucht`);
      setReceipt(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Buchung fehlgeschlagen"); }
    finally { setReceiptSaving(false); }
  };

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (f) await runScan(f);
    e.target.value = "";
  };

  const runScan = async (f) => {
    setScanning(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      if (docType !== "auto") fd.append("doc_type", docType);
      const r = await api.post("/inbound/scan", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await openPreview(r.data);
      toast.success("Beleg erkannt – bitte prüfen");
    } catch (e) { toast.error(e.response?.data?.detail || "Beleg konnte nicht gelesen werden"); }
    finally { setScanning(false); }
  };

  const loadFileBlob = async (fileId) => {
    try {
      const res = await api.get(`/inbound/file/${fileId}`, { responseType: "blob" });
      setFileMime(res.data.type || "");
      setFileUrl(URL.createObjectURL(res.data));
    } catch (e) { setFileUrl(null); }
  };

  const openPreview = async (data) => {
    setPreview({ ...data, doc_type: data.doc_type || "eingangsrechnung" });
    if (data.file_id) await loadFileBlob(data.file_id);
  };

  const closePreview = () => {
    if (fileUrl) URL.revokeObjectURL(fileUrl);
    setPreview(null); setFileUrl(null); setFileMime("");
  };

  const save = async () => {
    setSaving(true);
    try {
      const body = {
        file_id: preview.file_id, source_filename: preview.source_filename,
        doc_type: preview.doc_type, beleg_number: preview.beleg_number,
        beleg_date: preview.beleg_date, supplier_name: preview.supplier_name,
        supplier_vat: preview.supplier_vat, total_amount: preview.total_amount,
        currency: preview.currency, positions: preview.positions,
        supplier_id: preview.supplier_id || null, purchase_id: preview.purchase_id || null,
      };
      if (preview.id) await api.patch(`/inbound/documents/${preview.id}`, body);
      else await api.post("/inbound/documents", body);
      toast.success("Beleg gespeichert");
      closePreview(); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen"); }
    finally { setSaving(false); }
  };

  const openExisting = async (r) => {
    setPreview({ ...r, doc_type: r.doc_type || "eingangsrechnung", purchase_suggestions: [] });
    if (r.file_id) await loadFileBlob(r.file_id);
  };

  const del = async (r) => {
    if (!window.confirm(`Beleg "${r.beleg_number || r.source_filename || ""}" löschen?`)) return;
    try { await api.delete(`/inbound/documents/${r.id}`); toast.success("Gelöscht"); load(); }
    catch (e) { toast.error("Löschen fehlgeschlagen"); }
  };

  const setP = (k, v) => setPreview((p) => ({ ...p, [k]: v }));

  return (
    <div data-testid="page-belegscan">
      <PageHeader eyebrow="Einkauf" title="Belegscan"
        description="Eingangsbelege scannen oder hochladen – KI erkennt und ordnet automatisch zu." />

      {/* Scan / Upload */}
      <div className="bg-[var(--surface)] border border-[var(--border)] rounded-2xl p-4 mb-5">
        <div className="flex flex-wrap items-end gap-3">
          <div className="min-w-[180px]">
            <Label className="text-xs">Belegtyp</Label>
            <Select value={docType} onValueChange={setDocType}>
              <SelectTrigger data-testid="scan-doctype"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="auto">Automatisch erkennen</SelectItem>
                <SelectItem value="eingangsrechnung">Eingangsrechnung</SelectItem>
                <SelectItem value="lieferschein">Lieferschein</SelectItem>
                <SelectItem value="gutschrift">Gutschrift</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <input ref={fileRef} type="file" accept="application/pdf,image/*" className="hidden" onChange={onFile} data-testid="scan-file-input" />
          <input ref={camRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={onFile} data-testid="scan-cam-input" />
          <Button onClick={() => fileRef.current?.click()} disabled={scanning} data-testid="scan-upload-btn"
                  className="rounded-full bg-[var(--pop-pink)] text-white font-bold">
            {scanning ? <Loader2 size={16} className="mr-1 animate-spin" /> : <Upload size={16} className="mr-1" />} Datei hochladen
          </Button>
          <Button onClick={() => camRef.current?.click()} disabled={scanning} variant="outline" data-testid="scan-camera-btn" className="rounded-full font-bold">
            <Camera size={16} className="mr-1" /> Kamera-Scan
          </Button>
          <Button onClick={() => setFtpOpen(true)} variant="ghost" data-testid="scan-ftp-btn" className="rounded-full">
            <FolderSync size={16} className="mr-1" /> Ordner-Watch (FTP)
          </Button>
        </div>
        <p className="text-xs text-[var(--text-muted)] mt-2">PDF, JPG oder PNG. Auf dem Smartphone öffnet „Kamera-Scan" direkt die Kamera.</p>
      </div>

      {/* List */}
      <div className="bg-[var(--surface)] border border-[var(--border)] rounded-2xl overflow-hidden">
        <div className="flex items-center gap-2 p-3 border-b border-[var(--border)]">
          {[["all", "Alle"], ["unassigned", "Unzugeordnet"], ["assigned", "Zugeordnet"]].map(([v, l]) => (
            <button key={v} onClick={() => setFilter(v)} data-testid={`belegscan-filter-${v}`}
                    className={`rounded-full px-3 h-8 text-xs font-bold border ${filter === v ? "bg-[var(--pop-pink)] text-white border-[var(--pop-pink)]" : "bg-transparent text-[var(--text-muted)] border-[var(--border)]"}`}>{l}</button>
          ))}
        </div>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader><TableRow>
              <TableHead>Belegnr.</TableHead><TableHead>Datum</TableHead><TableHead>Lieferant</TableHead>
              <TableHead>Typ</TableHead><TableHead className="text-right">Betrag</TableHead>
              <TableHead>Status</TableHead><TableHead></TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={7} className="text-center py-10 text-[var(--text-muted)]">Lädt…</TableCell></TableRow>
              ) : rows.length === 0 ? (
                <TableRow><TableCell colSpan={7} className="text-center py-10 text-[var(--text-muted)]" data-testid="belegscan-empty">Noch keine Belege.</TableCell></TableRow>
              ) : rows.map((r) => (
                <TableRow key={r.id} data-testid={`belegscan-row-${r.id}`}>
                  <TableCell className="font-mono font-semibold">{r.beleg_number || "—"}</TableCell>
                  <TableCell>{r.beleg_date || "—"}</TableCell>
                  <TableCell className="max-w-[200px] truncate">{r.supplier_name_ref || r.supplier_name || "—"}</TableCell>
                  <TableCell>{TYPE_LABEL[r.doc_type] || "—"}</TableCell>
                  <TableCell className="text-right">{fmt(r.total_amount)}</TableCell>
                  <TableCell><span className={`text-xs font-semibold rounded-full px-2.5 py-1 ${STATUS_CLS(r.status)}`}>{r.status === "assigned" ? "Zugeordnet" : "Unzugeordnet"}</span></TableCell>
                  <TableCell className="text-right whitespace-nowrap">
                    {r.doc_type === "lieferschein" && (
                      <button onClick={() => openReceipt(r)} data-testid={`belegscan-receipt-${r.id}`}
                              title={r.goods_receipt_at ? "Bereits als Wareneingang gebucht – erneut buchen" : "In Wareneingang übernehmen"}
                              className={`p-1.5 ${r.goods_receipt_at ? "text-green-600" : "hover:text-[var(--pop-pink)]"}`}>
                        <PackagePlus size={16} />
                      </button>
                    )}
                    <button onClick={() => openExisting(r)} data-testid={`belegscan-view-${r.id}`} className="p-1.5 hover:text-[var(--pop-pink)]"><Eye size={16} /></button>
                    <button onClick={() => del(r)} data-testid={`belegscan-del-${r.id}`} className="p-1.5 hover:text-red-600"><Trash2 size={16} /></button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Preview dialog */}
      <Dialog open={!!preview} onOpenChange={(o) => !o && closePreview()}>
        <DialogContent className="max-w-4xl w-[calc(100vw-2rem)] max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Beleg prüfen & zuordnen</DialogTitle></DialogHeader>
          {preview && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {/* fields */}
              <div className="space-y-3" data-testid="belegscan-preview-fields">
                {preview.duplicate_warning && (
                  <div className="flex items-start gap-2 rounded-lg bg-red-50 border border-red-200 text-red-700 px-3 py-2 text-xs" data-testid="belegscan-duplicate-warning">
                    <AlertTriangle size={15} className="shrink-0 mt-0.5" />
                    <span>Möglicher Doppelbeleg: Belegnummer „{preview.duplicate_of?.beleg_number}" von {preview.duplicate_of?.supplier_name || "diesem Lieferanten"} existiert bereits{preview.duplicate_of?.beleg_date ? ` (${preview.duplicate_of.beleg_date})` : ""}.</span>
                  </div>
                )}
                <div><Label className="text-xs">Belegtyp</Label>
                  <Select value={preview.doc_type} onValueChange={(v) => setP("doc_type", v)}>
                    <SelectTrigger data-testid="preview-doctype"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="eingangsrechnung">Eingangsrechnung</SelectItem>
                      <SelectItem value="lieferschein">Lieferschein</SelectItem>
                      <SelectItem value="gutschrift">Gutschrift</SelectItem>
                    </SelectContent>
                  </Select></div>
                <div className="grid grid-cols-2 gap-2">
                  <div><Label className="text-xs">Belegnummer</Label>
                    <Input value={preview.beleg_number || ""} data-testid="preview-number" onChange={(e) => setP("beleg_number", e.target.value)} /></div>
                  <div><Label className="text-xs">Datum</Label>
                    <Input type="date" value={preview.beleg_date || ""} data-testid="preview-date" onChange={(e) => setP("beleg_date", e.target.value)} /></div>
                </div>
                <div><Label className="text-xs">Lieferant (Zuordnung)</Label>
                  <Select value={preview.supplier_id || "none"} onValueChange={(v) => {
                    const sid = v === "none" ? null : v;
                    const sup = suppliers.find((s) => s.id === sid);
                    setPreview((p) => ({ ...p, supplier_id: sid, supplier_name: sup ? sup.name : p.supplier_name }));
                  }}>
                    <SelectTrigger data-testid="preview-supplier"><SelectValue placeholder="— nicht zugeordnet —" /></SelectTrigger>
                    <SelectContent className="max-h-72">
                      <SelectItem value="none">— nicht zugeordnet —</SelectItem>
                      {suppliers.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  {preview.supplier_matched && <p className="text-[11px] text-green-600 mt-1">KI-Vorschlag: {preview.supplier_name}</p>}
                  {!preview.supplier_id && <p className="text-[11px] text-amber-600 mt-1">Kein Lieferant – wird als „Unzugeordnet" abgelegt.</p>}
                </div>
                {(preview.purchase_suggestions?.length > 0) && (
                  <div><Label className="text-xs">Offene Bestellung verknüpfen</Label>
                    <Select value={preview.purchase_id || "none"} onValueChange={(v) => setP("purchase_id", v === "none" ? null : v)}>
                      <SelectTrigger data-testid="preview-purchase"><SelectValue placeholder="— keine —" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">— keine —</SelectItem>
                        {preview.purchase_suggestions.map((p) => <SelectItem key={p.id} value={p.id}>{p.number} · {p.status} · {fmt(p.total)}</SelectItem>)}
                      </SelectContent>
                    </Select></div>
                )}
                <div className="grid grid-cols-2 gap-2">
                  <div><Label className="text-xs">Gesamtbetrag</Label>
                    <Input type="number" step="0.01" value={preview.total_amount ?? ""} data-testid="preview-total" onChange={(e) => setP("total_amount", e.target.value === "" ? null : Number(e.target.value))} /></div>
                  <div><Label className="text-xs">Währung</Label>
                    <Input value={preview.currency || "EUR"} data-testid="preview-currency" onChange={(e) => setP("currency", e.target.value)} /></div>
                </div>
                {(preview.positions?.length > 0) && (
                  <div>
                    <Label className="text-xs">Erkannte Positionen ({preview.positions.length})</Label>
                    <div className="mt-1 max-h-40 overflow-y-auto text-xs border border-[var(--border)] rounded-lg divide-y divide-[var(--border)]">
                      {preview.positions.map((p, i) => (
                        <div key={i} className="flex justify-between gap-2 px-2 py-1">
                          <span className="truncate">{p.quantity ? `${p.quantity}× ` : ""}{p.description}</span>
                          <span className="shrink-0 text-[var(--text-muted)]">{p.line_total != null ? fmt(p.line_total) : ""}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              {/* original preview */}
              <div className="bg-[var(--surface-alt)] rounded-xl border border-[var(--border)] min-h-[300px] flex items-center justify-center overflow-hidden" data-testid="belegscan-original">
                {!fileUrl ? <span className="text-xs text-[var(--text-muted)]">Vorschau lädt…</span>
                  : fileMime.includes("pdf") ? <iframe title="Beleg" src={fileUrl} className="w-full h-[460px]" />
                  : <img alt="Beleg" src={fileUrl} className="max-w-full max-h-[460px] object-contain" />}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={closePreview} className="rounded-full">Abbrechen</Button>
            <Button onClick={save} disabled={saving} data-testid="belegscan-save" className="rounded-full bg-[var(--pop-pink)] text-white font-bold">
              {saving ? "Speichern…" : "Beleg speichern"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <FtpConfigDialog open={ftpOpen} onClose={() => setFtpOpen(false)} />

      {/* Goods-receipt dialog */}
      <Dialog open={!!receipt} onOpenChange={(o) => !o && setReceipt(null)}>
        <DialogContent className="max-w-3xl w-[calc(100vw-2rem)] max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>In Wareneingang übernehmen</DialogTitle></DialogHeader>
          {receipt && (
            <div className="space-y-3" data-testid="receipt-dialog">
              <p className="text-xs text-[var(--text-muted)]">
                Lieferschein {receipt.meta?.beleg_number || ""} · {receipt.meta?.supplier_name || ""}. Artikel zuordnen, Menge/MHD/Lagerplatz prüfen, dann buchen.
              </p>
              {receipt.already && (
                <div className="rounded-lg bg-amber-50 border border-amber-200 text-amber-800 px-3 py-2 text-xs">
                  Dieser Beleg wurde bereits als Wareneingang gebucht. Erneutes Buchen legt neue Chargen an.
                </div>
              )}
              <div className="space-y-2">
                {receipt.lines.map((l, i) => (
                  <div key={i} className="rounded-xl border border-[var(--border)] p-2 space-y-2" data-testid={`receipt-line-${i}`}>
                    <div className="text-xs font-semibold truncate">{l.description || `Position ${i + 1}`}</div>
                    <div className="grid grid-cols-1 sm:grid-cols-12 gap-2">
                      <div className="sm:col-span-5">
                        <Label className="text-[10px]">Artikel</Label>
                        <ItemAutocomplete value={l.item_id} items={items}
                          onChange={(id) => setRL(i, "item_id", id)} testId={`receipt-item-${i}`} />
                      </div>
                      <div className="sm:col-span-2">
                        <Label className="text-[10px]">Menge</Label>
                        <Input type="number" value={l.quantity} data-testid={`receipt-qty-${i}`} onChange={(e) => setRL(i, "quantity", e.target.value)} />
                      </div>
                      <div className="sm:col-span-3">
                        <Label className="text-[10px]">MHD</Label>
                        <Input type="date" value={l.mhd} data-testid={`receipt-mhd-${i}`} onChange={(e) => setRL(i, "mhd", e.target.value)} />
                      </div>
                      <div className="sm:col-span-2">
                        <Label className="text-[10px]">Lagerplatz</Label>
                        <Input value={l.location} data-testid={`receipt-loc-${i}`} onChange={(e) => setRL(i, "location", e.target.value)} />
                      </div>
                    </div>
                  </div>
                ))}
                {receipt.lines.length === 0 && <p className="text-xs text-[var(--text-muted)]">Keine Positionen erkannt.</p>}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setReceipt(null)} className="rounded-full">Abbrechen</Button>
            <Button onClick={confirmReceipt} disabled={receiptSaving} data-testid="receipt-confirm" className="rounded-full bg-[var(--pop-pink)] text-white font-bold">
              {receiptSaving ? "Buche…" : "Wareneingang buchen"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function FtpConfigDialog({ open, onClose }) {
  const [cfg, setCfg] = useState({ enabled: false, host: "", port: 21, username: "", path: "/", password: "" });
  const [hasPw, setHasPw] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) return;
    api.get("/inbound/ftp-config").then((r) => { setCfg({ ...r.data, password: "" }); setHasPw(r.data.has_password); }).catch(() => {});
  }, [open]);
  const save = async () => {
    setBusy(true);
    try {
      const body = { ...cfg };
      if (!body.password) delete body.password;
      await api.put("/inbound/ftp-config", body);
      toast.success("FTP-Konfiguration gespeichert");
      onClose();
    } catch (e) { toast.error("Speichern fehlgeschlagen"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>Ordner-Watch (FTP/SFTP)</DialogTitle></DialogHeader>
        <div className="space-y-3" data-testid="ftp-config">
          <p className="text-xs text-[var(--text-muted)]">Ein vom Internet erreichbarer FTP-Ordner wird stündlich automatisch nach neuen Belegen durchsucht.</p>
          <label className="flex items-center gap-2 text-sm font-semibold">
            <input type="checkbox" checked={cfg.enabled} data-testid="ftp-enabled" onChange={(e) => setCfg({ ...cfg, enabled: e.target.checked })} /> Aktiviert
          </label>
          <div className="grid grid-cols-3 gap-2">
            <div className="col-span-2"><Label className="text-xs">Host</Label><Input value={cfg.host} data-testid="ftp-host" onChange={(e) => setCfg({ ...cfg, host: e.target.value })} /></div>
            <div><Label className="text-xs">Port</Label><Input type="number" value={cfg.port} data-testid="ftp-port" onChange={(e) => setCfg({ ...cfg, port: Number(e.target.value) })} /></div>
          </div>
          <div><Label className="text-xs">Benutzer</Label><Input value={cfg.username} data-testid="ftp-user" onChange={(e) => setCfg({ ...cfg, username: e.target.value })} /></div>
          <div><Label className="text-xs">Passwort {hasPw && <span className="text-green-600">(gesetzt)</span>}</Label>
            <Input type="password" value={cfg.password} placeholder={hasPw ? "••••• (unverändert lassen)" : ""} data-testid="ftp-pass" onChange={(e) => setCfg({ ...cfg, password: e.target.value })} /></div>
          <div><Label className="text-xs">Pfad</Label><Input value={cfg.path} data-testid="ftp-path" onChange={(e) => setCfg({ ...cfg, path: e.target.value })} /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-full">Abbrechen</Button>
          <Button onClick={save} disabled={busy} data-testid="ftp-save" className="rounded-full bg-[var(--pop-pink)] text-white font-bold">Speichern</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
